import { Box, Stack, Typography } from '@mui/material';
import { useContext } from 'react';

import { Co2TourGeneralSection } from '@/domain/impact-calculate-tour/entities/impact-tour.entity';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { QuizPanelLayout } from '@/ui/components/quiz-panel/QuizPanelLayout';
import { translate } from '@/ui/i18n';
import { QuizFormSeparator } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizFormSeparator';
import { ImpactForm } from '@/ui/pages/app/components/quiz-panel/co2/ImpactForm';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { QuizPanelHideCompletedCheckBox } from '@/ui/pages/app/components/quiz-panel/QuizPanelHideCompletedCheckBox';

export function QuizPanelCustomTourCo2({
  projectId,
  quizSubType,
  setIsQuizLayoutDisplayed,
}: {
  projectId: number;
  quizSubType: string;
  setIsQuizLayoutDisplayed?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { panelOpen, setPanelOpen } = useContext(ImpactQuestionContext);

  const hideLayout = () => {
    if (setIsQuizLayoutDisplayed && setPanelOpen) {
      setIsQuizLayoutDisplayed(false);
      setTimeout(() => {
        setPanelOpen(false);
      }, 500);
    }
  };

  return (
    <Box>
      <QuizPanelLayout panelOpen={panelOpen} setPanelOpen={setPanelOpen}>
        <SidePanelHeader onClickClose={hideLayout} />
        <QuizPanelHideCompletedCheckBox />
        <Co2Quiz projectId={projectId} quizSubType={quizSubType} />
      </QuizPanelLayout>
    </Box>
  );
}

function SidePanelHeader({ onClickClose }: { onClickClose: () => void }) {
  return (
    <>
      <Stack direction={'row'} alignItems={'center'}>
        <CloseButton sx={{ mr: 1, ml: -2 }} onClick={onClickClose} />
        <Typography variant="h6">{translate('Quiz.questions-co2')}</Typography>
      </Stack>
    </>
  );
}

function Co2Quiz({
  projectId,
  quizSubType,
}: {
  projectId: number;
  quizSubType: string;
}) {
  const section = quizSubType as Co2TourGeneralSection;
  switch (section) {
    case 'CREATION':
      return <Co2QuizCreation projectId={projectId} />;
    case 'MANAGEMENT':
      return <Co2QuizManagement projectId={projectId} />;
    case 'COMMUNICATION':
      return <Co2QuizCommunication projectId={projectId} />;
  }
}

function Co2QuizCreation({ projectId }: { projectId: number }) {
  return (
    <Box>
      <Box id={'SCENOGRAPHY-form'}>
        <QuizFormSeparator label={translate('Co2.Sections.SCENOGRAPHY')} />
        <ImpactForm
          projectId={projectId}
          sector={'SITE'}
          category={'SCENOGRAPHY_NO_RECYCLABLE'}
        />
        <ImpactForm
          projectId={projectId}
          sector={'SITE'}
          category={'SCENOGRAPHY_RECYCLABLE'}
        />
      </Box>
      <Box id={'TEAM_MOBILITY-form'}>
        <QuizFormSeparator
          label={translate(
            'ProjectsTour.GeneralCo2Quiz.Categories.TEAM_MOBILITY',
          )}
        />
        <ImpactForm
          projectId={projectId}
          sector={'ARTISTS'}
          category={'TEAM_MOBILITY'}
        />
      </Box>
      <Box id={'REPEATS_AND_MATERIAL-form'}>
        <QuizFormSeparator
          label={translate(
            'ProjectsTour.GeneralCo2Quiz.Categories.REPEATS_AND_MATERIAL',
          )}
        />
        <ImpactForm
          projectId={projectId}
          sector={'SITE'}
          category={'BUILDING_HEATING'}
        />
        <ImpactForm
          projectId={projectId}
          sector={'SITE'}
          category={'EQUIPMENT'}
        />
        <ImpactForm projectId={projectId} sector={'SITE'} category={'CARGO'} />
      </Box>
    </Box>
  );
}

function Co2QuizManagement({ projectId }: { projectId: number }) {
  return (
    <Box>
      <Box id={'TEAM_MOBILITY-form'}>
        <QuizFormSeparator
          label={translate(
            'ProjectsTour.GeneralCo2Quiz.Categories.TEAM_MOBILITY',
          )}
        />
        <ImpactForm
          projectId={projectId}
          sector={'TEAM'}
          category={'TEAM_MOBILITY'}
        />
      </Box>
      <Box id={'CATERING-form'}>
        <QuizFormSeparator label={translate('Co2.Sections.CATERING')} />
        <ImpactForm
          projectId={projectId}
          sector={'TEAM'}
          category={'CATERING_MEAL'}
        />
      </Box>
      <Box id={'OFFICE-form'}>
        <QuizFormSeparator
          label={translate('ProjectsTour.GeneralCo2Quiz.Categories.OFFICE')}
        />
        <ImpactForm
          projectId={projectId}
          sector={'TEAM'}
          category={'OFFICE_SUPPLIES_NEW'}
        />
        <ImpactForm
          projectId={projectId}
          sector={'TEAM'}
          category={'OFFICE_SUPPLIES_REFURBISH'}
        />
        <ImpactForm
          projectId={projectId}
          sector={'TEAM'}
          category={'BUILDING_HEATING'}
        />
      </Box>
    </Box>
  );
}

function Co2QuizCommunication({ projectId }: { projectId: number }) {
  return (
    <Box>
      <Box id={'VIDEO_AND_DIGITAL-form'}>
        <QuizFormSeparator
          label={translate(
            'ProjectsTour.GeneralCo2Quiz.Categories.VIDEO_AND_DIGITAL',
          )}
        />
        <ImpactForm
          projectId={projectId}
          sector={'PUBLIC'}
          category={'DIGITAL'}
        />
      </Box>
    </Box>
  );
}
